import { useState } from "react";
import { useT } from "@/lib/i18n";
import { formatPeriod } from "@/lib/resumeUtils";
import ExperienceWizard from "./ExperienceWizard";
import YmPicker from "./YmPicker";
import PopoutTextarea from "@/components/PopoutTextarea";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Sparkles, Trash2, ChevronDown, ChevronUp, Loader2, RefreshCw, Lightbulb, X } from "lucide-react";

function ExperienceCard({ item, index, onChange, onDelete, onOptimize, testPrefix, onDragStart, onDragOver, onDrop }) {
  const { t, lang } = useT();
  const [open, setOpen] = useState(false);
  const set = (field, value) => onChange({ ...item, [field]: value });
  const period = formatPeriod(item, lang, lang === "en" ? "present" : "heden") || t("rbNoPeriod");

  return (
    <div
      className="ed-card"
      data-testid={`${testPrefix}-card-${index}`}
      draggable={!!onDragStart}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDrop={onDrop}
    >
      <div
        data-testid={`${testPrefix}-header-${index}`}
        onClick={() => setOpen(!open)}
        className="p-4 flex items-center justify-between gap-3 flex-wrap cursor-pointer hover:bg-neutral-50 transition-colors"
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setOpen(!open); } }}
      >
        <div className="min-w-0 flex items-center gap-3">
          {/* Drag handle — the entire card is draggable but this glyph
              signals it to the user. Stops propagation so grabbing it
              doesn't also toggle the accordion. */}
          <span
            className="opacity-40 cursor-grab active:cursor-grabbing select-none shrink-0"
            title={t("rbDragHint")}
            onClick={(e) => e.stopPropagation()}
          >⋮⋮</span>
          <div className="min-w-0">
            <p className="font-serif text-lg leading-tight truncate">
              {item.role || "—"}{item.company ? <span className="italic text-neutral-600"> · {item.company}</span> : null}
            </p>
            <p className="font-mono text-[0.65rem] uppercase tracking-[0.15em] text-neutral-500 mt-0.5">{period}</p>
          </div>
        </div>
        <div className="flex gap-2 shrink-0" onClick={(e) => e.stopPropagation()}>
          <button
            data-testid={`${testPrefix}-ai-${index}`}
            onClick={onOptimize}
            className="p-2 border border-neutral-300 hover:border-neutral-900 hover:bg-neutral-900 hover:text-white transition-colors"
            title={t("rbAiOptimize")}
          >
            <Sparkles size={14} strokeWidth={1.5} />
          </button>
          <button
            data-testid={`${testPrefix}-toggle-${index}`}
            onClick={() => setOpen(!open)}
            className="p-2 border border-neutral-300 hover:border-neutral-900 hover:bg-neutral-900 hover:text-white transition-colors"
            title={t("edit")}
          >
            {open ? <ChevronUp size={14} strokeWidth={1.5} /> : <ChevronDown size={14} strokeWidth={1.5} />}
          </button>
          <button
            data-testid={`${testPrefix}-delete-${index}`}
            onClick={onDelete}
            className="p-2 border border-neutral-300 hover:border-rose-700 hover:bg-rose-700 hover:text-white transition-colors"
            title={t("delete")}
          >
            <Trash2 size={14} strokeWidth={1.5} />
          </button>
        </div>
      </div>

      {open && (
        <div className="px-4 pb-5 pt-1 border-t border-neutral-200 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="ed-label">{t("rbRole")}</label>
              <input className="ed-input" value={item.role} onChange={(e) => set("role", e.target.value)} />
            </div>
            <div>
              <label className="ed-label">{t("rbCompany")}</label>
              <input className="ed-input" value={item.company} onChange={(e) => set("company", e.target.value)} />
            </div>
            <div>
              <label className="ed-label">{t("rbLocation")}</label>
              <input className="ed-input" value={item.location} onChange={(e) => set("location", e.target.value)} />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="ed-label">{t("rbFrom")}</label>
              <YmPicker testId={`${testPrefix}-start-${index}`} value={item.start} onChange={(v) => set("start", v)} />
            </div>
            <div>
              <label className="ed-label">{t("rbUntil")}</label>
              <YmPicker testId={`${testPrefix}-end-${index}`} value={item.end} onChange={(v) => set("end", v)} disabled={item.current} />
              <label className="inline-flex items-center gap-2 mt-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={item.current}
                  onChange={(e) => set("current", e.target.checked)}
                  className="w-4 h-4 accent-neutral-900"
                />
                <span className="text-xs font-mono text-neutral-600">{t("rbCurrentRole")}</span>
              </label>
            </div>
          </div>
          <div>
            <label className="ed-label">{t("rbDescriptionLabel")}</label>
            <PopoutTextarea
              dataTestId={`${testPrefix}-description-${index}`}
              className="min-h-[7rem]"
              value={item.description}
              onChange={(next) => set("description", next)}
              title={`${item.role || t("rbDescriptionLabel")}${item.company ? ` · ${item.company}` : ""}`}
              rows={6}
            />
          </div>
          <AchievementsField
            item={item}
            index={index}
            testPrefix={testPrefix}
            onChange={(next) => set("achievements", next)}
            t={t}
          />
        </div>
      )}
    </div>
  );
}


function AchievementsField({ item, index, testPrefix, onChange, t }) {
  const [busy, setBusy] = useState(null); // 'suggest' | 'polish' | null
  // One-time explainer bubble — dismissed forever once the user closes
  // it. Rendered only for the FIRST experience card (index 0) so it
  // shows once per session instead of every card. Uses localStorage so
  // it survives reloads but is per-browser (fine for a coach-style hint).
  const [hintDismissed, setHintDismissed] = useState(() => {
    try { return localStorage.getItem("jg.achievements.hint.v1") === "1"; }
    catch { return false; }
  });
  const dismissHint = () => {
    try { localStorage.setItem("jg.achievements.hint.v1", "1"); } catch {}
    setHintDismissed(true);
  };
  const value = item.achievements || "";
  const hasContent = value.trim().length > 0;
  const canSuggest = (item.role || "").trim().length > 0 && (item.description || "").trim().length > 20;

  const call = async (kind) => {
    if (busy) return;
    setBusy(kind);
    try {
      const before = value;
      const endpoint = kind === "suggest" ? "/resumes/ai/achievements/suggest" : "/resumes/ai/achievements/polish";
      const { data } = await api.post(endpoint, {
        role: item.role || "",
        company: item.company || "",
        description: item.description || "",
        current_text: kind === "polish" ? value : "",
        lang: "nl",
      });
      const next = (data?.achievements || "").trim();
      if (!next) {
        toast.error(t("rbAchievementsFail"));
        return;
      }
      onChange(next);
      toast.success(
        kind === "suggest" ? t("rbAchievementsSuggestDone") : t("rbAchievementsPolishDone"),
        {
          duration: 7000,
          action: {
            label: t("rbAchievementsUndo"),
            onClick: () => onChange(before),
          },
        },
      );
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("rbAchievementsFail"));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div>
      {!hintDismissed && index === 0 && (
        <div
          data-testid={`${testPrefix}-achievements-hint`}
          className="mb-4 border-2 border-emerald-700 bg-emerald-50 p-4 flex gap-3 items-start"
        >
          <Lightbulb size={18} strokeWidth={1.75} className="text-emerald-800 shrink-0 mt-0.5" />
          <div className="min-w-0 flex-1 space-y-2">
            <p className="font-mono text-[0.62rem] uppercase tracking-[0.15em] text-emerald-800">
              {t("rbAchievementsHintTitle")}
            </p>
            <p className="text-sm text-neutral-800 leading-relaxed">
              {t("rbAchievementsHintBody")}
            </p>
            <ul className="text-xs text-neutral-700 space-y-1 pl-4 leading-relaxed">
              <li className="list-disc">
                <span className="font-mono text-neutral-500">{t("rbAchievementsHintExOldLabel")}</span>{" "}
                <span className="italic text-neutral-500">"{t("rbAchievementsHintExOld")}"</span>
              </li>
              <li className="list-disc">
                <span className="font-mono text-emerald-700">{t("rbAchievementsHintExNewLabel")}</span>{" "}
                <span className="italic text-neutral-900">"{t("rbAchievementsHintExNew")}"</span>
              </li>
            </ul>
            <p className="text-xs text-neutral-600 leading-relaxed pt-1">
              {t("rbAchievementsHintFooter")}
            </p>
          </div>
          <button
            type="button"
            data-testid={`${testPrefix}-achievements-hint-close`}
            onClick={dismissHint}
            className="shrink-0 text-emerald-800 hover:text-neutral-900 transition-colors p-0.5"
            aria-label={t("dismiss") || "Sluit"}
          >
            <X size={16} strokeWidth={1.75} />
          </button>
        </div>
      )}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <label className="ed-label">{t("rbAchievementsLabel")}</label>
        <div className="flex items-center gap-2">
          <button
            type="button"
            data-testid={`${testPrefix}-achievements-suggest-${index}`}
            onClick={() => call("suggest")}
            disabled={!!busy || !canSuggest}
            title={canSuggest ? t("rbAchievementsSuggestHint") : t("rbAchievementsSuggestNeedsDesc")}
            className="inline-flex items-center gap-1.5 font-mono text-[0.6rem] uppercase tracking-[0.12em] px-2.5 py-1.5 border border-neutral-300 hover:border-neutral-900 hover:bg-neutral-900 hover:text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {busy === "suggest" ? <Loader2 size={11} strokeWidth={1.75} className="animate-spin" /> : <Sparkles size={11} strokeWidth={1.75} />}
            {busy === "suggest" ? t("rbAchievementsBusy") : t("rbAchievementsSuggestCta")}
          </button>
          <button
            type="button"
            data-testid={`${testPrefix}-achievements-polish-${index}`}
            onClick={() => call("polish")}
            disabled={!!busy || !hasContent}
            title={hasContent ? t("rbAchievementsPolishHint") : t("rbAchievementsPolishNeedsText")}
            className="inline-flex items-center gap-1.5 font-mono text-[0.6rem] uppercase tracking-[0.12em] px-2.5 py-1.5 border border-neutral-300 hover:border-neutral-900 hover:bg-neutral-900 hover:text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {busy === "polish" ? <Loader2 size={11} strokeWidth={1.75} className="animate-spin" /> : <RefreshCw size={11} strokeWidth={1.75} />}
            {busy === "polish" ? t("rbAchievementsBusy") : t("rbAchievementsPolishCta")}
          </button>
        </div>
      </div>
      <p className="font-mono text-[0.6rem] text-neutral-500 mt-1 mb-2 leading-relaxed">
        {t("rbAchievementsHint")}
      </p>
      <PopoutTextarea
        dataTestId={`${testPrefix}-achievements-${index}`}
        className="min-h-[6rem] font-mono text-sm"
        value={value}
        onChange={(next) => onChange(next)}
        title={`${t("rbAchievementsLabel")}${item.role ? ` · ${item.role}` : ""}`}
        rows={5}
        placeholder={t("rbAchievementsPh")}
      />
    </div>
  );
}

export default function ExperienceList({ items, onChange, addLabelKey, testPrefix }) {
  const { t } = useT();
  const [wizard, setWizard] = useState(null); // {mode, initial}

  const saveFromWizard = (exp) => {
    const exists = items.some((x) => x.id === exp.id);
    onChange(exists ? items.map((x) => (x.id === exp.id ? exp : x)) : [...items, exp]);
    setWizard(null);
  };

  // HTML5 drag-and-drop reorder. Same pattern as References/Skills — the
  // dragged card's index is stored in `dataTransfer` and the drop target
  // splices the array. Auto-sort in `ResumeEditor.jsx` still runs on the
  // NEXT open (HEDEN-first then start-date DESC), so free manual reorder
  // now lets the user override the default ordering while editing.
  const move = (from, to) => {
    if (from === to || from < 0 || to < 0 || from >= items.length || to >= items.length) return;
    const next = [...items];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    onChange(next);
  };

  return (
    <div className="space-y-3">
      {items.map((item, i) => (
        <ExperienceCard
          key={item.id}
          item={item}
          index={i}
          testPrefix={testPrefix}
          onChange={(next) => onChange(items.map((x) => (x.id === next.id ? next : x)))}
          onDelete={() => onChange(items.filter((x) => x.id !== item.id))}
          onOptimize={() => setWizard({ mode: "optimize", initial: item })}
          onDragStart={(e) => { e.dataTransfer.setData("text/plain", String(i)); e.dataTransfer.effectAllowed = "move"; }}
          onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; }}
          onDrop={(e) => {
            e.preventDefault();
            const from = parseInt(e.dataTransfer.getData("text/plain"), 10);
            if (!Number.isNaN(from)) move(from, i);
          }}
        />
      ))}
      <button
        data-testid={`${testPrefix}-add`}
        onClick={() => setWizard({ mode: "add", initial: null })}
        className="ed-btn ed-btn-outline"
      >
        <Plus size={14} strokeWidth={1.5} /> {t(addLabelKey)}
      </button>

      {wizard && (
        <ExperienceWizard
          mode={wizard.mode}
          initial={wizard.initial}
          onClose={() => setWizard(null)}
          onSave={saveFromWizard}
        />
      )}
    </div>
  );
}
